import { estimatedCandela } from './estimatedPhotometry.js';
import { buildingVisibility } from './buildingVisibility.js';
import { wallIntersection, siteBBox, insideSite, ptInPoly } from './geometry.js';
import { lightPose, LIGHTING_VERSION } from './lightingData.js';
import { parseIES, iesCandela } from './photometry.js';
import { solarPosition } from './solar.js';
import { scheduledGroups } from './lightingTime.js';
import { RAD, DEG, WALL_THICKNESS } from './constants.js';

const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
export const surfaceNormal = (kind, heading = 0) => kind === 'vertical' ? { x: Math.cos(heading * RAD), y: Math.sin(heading * RAD), z: 0 } : { x: 0, y: 0, z: 1 };
export function lightAxes(light) {
  const p = light.pan * RAD, t = light.tilt * RAD;
  return { forward: { x: Math.cos(p) * Math.cos(t), y: Math.sin(p) * Math.cos(t), z: -Math.sin(t) },
    zero: { x: Math.cos(p) * Math.sin(t), y: Math.sin(p) * Math.sin(t), z: Math.cos(t) },
    cross: { x: -Math.sin(p), y: Math.cos(p), z: 0 } };
}

// A small spatial hierarchy keeps light and sky rays independent of GPU/display
// state without testing every building wall for every surface sample.
function bounds(w) {
  const pad = (w.thickness ?? WALL_THICKNESS) / 2;
  return [Math.min(w.x1, w.x2) - pad, Math.min(w.y1, w.y2) - pad, w.bottom || 0, Math.max(w.x1, w.x2) + pad, Math.max(w.y1, w.y2) + pad, w.h];
}
function hierarchy(items) {
  if (!items.length) return null;
  const b = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
  for (const i of items) for (let k = 0; k < 3; k++) { b[k] = Math.min(b[k], i.b[k]); b[k + 3] = Math.max(b[k + 3], i.b[k + 3]); }
  if (items.length <= 8) return { b, items };
  const axis = [0, 1, 2].sort((a, c) => (b[c + 3] - b[c]) - (b[a + 3] - b[a]))[0];
  items.sort((a, c) => (a.b[axis] + a.b[axis + 3]) - (c.b[axis] + c.b[axis + 3]));
  const mid = items.length >> 1;
  return { b, left: hierarchy(items.slice(0, mid)), right: hierarchy(items.slice(mid)) };
}
function intersects(b, p, q) {
  let a = 0, z = 1;
  for (let k = 0; k < 3; k++) {
    const delta = q[k] - p[k];
    if (Math.abs(delta) < 1e-12) { if (p[k] < b[k] || p[k] > b[k + 3]) return false; }
    else { const x = (b[k] - p[k]) / delta, y = (b[k + 3] - p[k]) / delta; a = Math.max(a, Math.min(x, y)); z = Math.min(z, Math.max(x, y)); if (z < a) return false; }
  }
  return true;
}
export function lightRayIndex(walls, glassTransmission = null) {
  const root = hierarchy(walls.map(w => ({ w, b: bounds(w) })));
  return (from, to) => {
    const p = [from.x, from.y, from.z], q = [to.x, to.y, to.z], glasses = [];
    function visit(node) {
      if (!node || !intersects(node.b, p, q)) return false;
      if (!node.items) return visit(node.left) || visit(node.right);
      for (const { w, b } of node.items) {
        // Reject each leaf neighbour before its exact prism test. Scalar
        // arguments avoid array-iterator overhead in this very hot loop.
        if (!intersects(b, p, q)) continue;
        const hit = wallIntersection(p[0], p[1], p[2], q[0], q[1], q[2], w);
        if (!hit || hit.leave <= 1e-7 || hit.enter >= 1 - 1e-7) continue;
        if (w.kind !== 'glass') return true;
        if (!glasses.some(t => Math.abs(t - hit.enter) < 1e-6)) glasses.push(hit.enter);
      }
      return false;
    }
    if (visit(root)) return { low: 0, high: 0 };
    return !glasses.length ? { low: 1, high: 1 } : glassTransmission == null ? { low: 0, high: 1 } : { low: glassTransmission ** glasses.length, high: glassTransmission ** glasses.length };
  };
}

// First opaque surface for the illustrative diffuse-light cache. Numeric lux
// continues to use lightRayIndex; this query does not alter its transport.
export function lightSurfaceIndex(walls) {
  const root = hierarchy(walls.filter(w => w.kind !== 'glass').map(w => ({ w, b: bounds(w) })));
  return (from, to) => {
    const p=[from.x,from.y,from.z], q=[to.x,to.y,to.z],length=Math.hypot(to.x-from.x,to.y-from.y,to.z-from.z); let nearest=null, time=1;
    function visit(node) {
      if (!node || !intersects(node.b,p,q)) return;
      if (!node.items) { visit(node.left); visit(node.right); return; }
      for (const {w} of node.items) {
        const hit=wallIntersection(...p,...q,w);
        if (!hit || hit.enter*length <= .0001 || hit.enter >= time) continue;
        time=hit.enter; nearest=w;
      }
    }
    visit(root); if (!nearest) return null;
    const point={x:p[0]+(q[0]-p[0])*time,y:p[1]+(q[1]-p[1])*time,z:p[2]+(q[2]-p[2])*time};
    let normal;
    if (Math.abs(point.z-nearest.h)<.002) normal={x:0,y:0,z:1};
    else if (Math.abs(point.z-(nearest.bottom||0))<.002) normal={x:0,y:0,z:-1};
    else {
      let edge=[nearest.x2-nearest.x1,nearest.y2-nearest.y1];
      if (nearest.roofPts || nearest.wallPts) {
        let distance=Infinity;
        for (const ring of [nearest.roofPts || nearest.wallPts,...(nearest.roofHoles||[])]) for(let i=0;i<ring.length;i++) {
          const a=ring[i], b=ring[(i+1)%ring.length], dx=b.x-a.x, dy=b.y-a.y;
          const t=Math.max(0,Math.min(1,((point.x-a.x)*dx+(point.y-a.y)*dy)/(dx*dx+dy*dy||1)));
          const d=Math.hypot(point.x-a.x-dx*t,point.y-a.y-dy*t);
          if(d<distance){distance=d;edge=[dx,dy];}
        }
      }
      const length=Math.hypot(...edge)||1; normal={x:-edge[1]/length,y:edge[0]/length,z:0};
      if(!nearest.roofPts && !nearest.wallPts){const along=((point.x-nearest.x1)*edge[0]+(point.y-nearest.y1)*edge[1])/length;if(Math.min(Math.abs(along),Math.abs(along-length))<.002)normal={x:edge[0]/length,y:edge[1]/length,z:0};}
      if(dot(normal,{x:to.x-from.x,y:to.y-from.y,z:to.z-from.z})>0){normal.x*=-1;normal.y*=-1;}
    }
    return {point,normal,wall:nearest};
  };
}

export function makeLightingScene(S, scenario, { definitionIds = [] } = {}) {
  const state = scenario.sceneState || S.sceneState;
  const document = { ...S, sceneState: state, floors: (S.floors || []).map(f => ({ ...f, plan: { ...f.plan, sceneState: state } })) };
  const context = buildingVisibility(document), definitions = new Map();
  const fixtures = context.levels.flatMap(f => (f.plan.lights || []).map(l => ({ ...lightPose(f.plan, l), elevation: f.elevation, floorId: f.id })));
  const used = new Set([...fixtures.map(l => l.definitionId), ...definitionIds]);
  for (const d of S.lighting?.definitions || []) if (used.has(d.id)) definitions.set(d.id, { ...d, data: d.kind === 'ies' ? parseIES(d.sourceText) : null });
  const env = S.lighting?.environment || {}, sun = scenario.daylight ? solarPosition(scenario.timestamp, env.latitude, env.longitude, env.north) : null;
  const trace = lightRayIndex(context.walls, env.glassTransmission);
  const extent = Math.max(100, ...context.walls.flatMap(w => [Math.abs(w.x1), Math.abs(w.x2), Math.abs(w.y1), Math.abs(w.y2), Math.abs(w.h)])) * 4 + 100;
  const sky = Array.from({ length: 48 }, (_, i) => { const z = (i + .5) / 48, a = i * Math.PI * (3 - Math.sqrt(5)), r = Math.sqrt(1 - z * z); return { x: r * Math.cos(a), y: r * Math.sin(a), z }; });
  const scheduled = scheduledGroups(S.lighting, scenario);
  return { context, definitions, fixtures, scenario, env, sun, trace, extent, sky, groupLevels: scheduled.levels, scheduleIssues: scheduled.issues };
}

export function illuminanceAt(scene, point, normal = { x: 0, y: 0, z: 1 }, { approximateNearField = false } = {}) {
  const { scenario, definitions, trace } = scene;
  let low = 0, high = 0, direct = 0, sky = 0, electric = 0, shade = null;
  const issues = new Set();
  if (scenario.lightsOn && scene.scheduleIssues.length && scene.fixtures.some(l => l.enabled && scene.groupLevels[l.group] === null)) {
    high = Infinity; scene.scheduleIssues.forEach(i => issues.add(i));
  }
  function add(value, visibility, component) {
    low += value * visibility.low; high += value * visibility.high;
    if (visibility.low !== visibility.high) issues.add('Glazing transmission unknown');
    if (component === 'direct') direct += value * visibility.low;
    if (component === 'sky') sky += value * visibility.low;
    if (component === 'electric') electric += value * visibility.low;
  }
  if (scenario.lightsOn) for (const light of scene.fixtures) {
    const level = light.enabled ? (light.dimming ?? 1) * (light.maintenance ?? 1) * (Object.hasOwn(scene.groupLevels, light.group) ? scene.groupLevels[light.group] : 1) : 0;
    if (!level) continue;
    const def = definitions.get(light.definitionId);
    if (light.invalidHost || !def) { issues.add(light.invalidHost ? 'A light has a missing pole' : 'A light is missing its definition'); high = Infinity; continue; }
    const emitter = { x: light.x, y: light.y, z: light.h + light.elevation };
    const dx = emitter.x - point.x, dy = emitter.y - point.y, dz = emitter.z - point.z, distance = Math.hypot(dx, dy, dz);
    if (distance < 1e-6) { issues.add('Surface intersects light emitter'); high = Infinity; continue; }
    const toward = { x: dx / distance, y: dy / distance, z: dz / distance }, cosine = Math.max(0, dot(normal, toward));
    if (!cosine) continue;
    const visibility = trace(point, emitter); if (!visibility.high) continue;
    const radius = def.data ? Math.max(...def.data.size) : def.size;
    if (distance < Math.max(.05, radius * 5)) {
      // Numerical assessments retain the photometric validity limit. The
      // interactive reflection cache instead needs the same point-source
      // approximation used by BOTH GPU renderers, not a zero-light lower bound.
      if (!approximateNearField || distance < .05) { issues.add('Too close to emitter for far-field photometry'); high = Infinity; continue; }
      issues.add('Near-field light uses a point-source preview approximation');
    }
    const outgoing = { x: -toward.x, y: -toward.y, z: -toward.z }, axes = lightAxes(light);
    const theta = Math.acos(Math.max(-1, Math.min(1, dot(outgoing, axes.forward)))) * DEG;
    const phi = Math.atan2(dot(outgoing, axes.cross), dot(outgoing, axes.zero)) * DEG;
    const cd = def.data ? iesCandela(def.data, theta, phi) : estimatedCandela(def, theta);
    add(cd * level * cosine / (distance * distance), visibility, 'electric');
  }
  if (scenario.daylight) {
    if (!scene.sun) { issues.add('Set site location and scenario time for daylight'); high = Infinity; }
    else {
      const direction = scene.sun.direction;
      if (scene.sun.elevation > 0) {
        const end = { x: point.x + direction.x * scene.extent, y: point.y + direction.y * scene.extent, z: point.z + direction.z * scene.extent };
        const visibility = trace(point, end); shade = visibility.high === 0 ? 'shade' : visibility.low === visibility.high ? 'sun' : 'unknown';
        add(scenario.directLux * Math.max(0, dot(normal, direction)), visibility, 'direct');
      } else shade = 'below-horizon';
      // Uniform sky luminance, normalized to the user's unobstructed horizontal
      // diffuse illuminance. No invented weather-to-lux conversion or bounces.
      if (scenario.diffuseLux > 0) for (const direction of scene.sky) {
        const cosine = Math.max(0, dot(normal, direction)); if (!cosine) continue;
        const end = { x: point.x + direction.x * scene.extent, y: point.y + direction.y * scene.extent, z: point.z + direction.z * scene.extent };
        add(scenario.diffuseLux * 2 / scene.sky.length * cosine, trace(point, end), 'sky');
      }
    }
  }
  return { low, high, direct, sky, electric, shade, issues: [...issues] };
}

export function lightingState(value, requiredLux) {
  return value.high < requiredLux ? 'shortfall' : value.low >= requiredLux ? 'met' : 'unknown';
}

export function lightingGrid(scene, level, surface, maxCells = 3000) {
  const review = scene.scenario.layoutReview?.floorId === level.id ? scene.scenario.layoutReview : null;
  if (review) surface = { ...surface, kind: 'horizontal', height: review.plane, step: review.step, minimumLux: review.targetLux * review.uniformity };
  const area = review?.areaId ? level.plan.areas.find(a => a.id === review.areaId) : null;
  const bb = siteBBox(level.plan); if (!bb) return null;
  const width = bb.x1 - bb.x0, depth = bb.y1 - bb.y0;
  let step = Math.max(surface.step, Math.sqrt(width * depth / maxCells));
  let nx = Math.ceil(width / step), ny = Math.ceil(depth / step);
  while (nx * ny > maxCells) { step *= 1.02; nx = Math.ceil(width / step); ny = Math.ceil(depth / step); }
  const n = nx * ny, sx = new Float32Array(n), sy = new Float32Array(n), inSite = new Uint8Array(n);
  const low = new Float32Array(n), high = new Float32Array(n), shade = new Uint8Array(n), states = new Uint8Array(n);
  const normal = surfaceNormal(surface.kind, surface.heading), summary = { samples: 0, met: 0, shortfall: 0, unknown: 0, min: Infinity, max: 0, mean: 0 }, issues = new Set();
  for (let i = 0; i < n; i++) {
    sx[i] = bb.x0 + (i % nx + .5) * step; sy[i] = bb.y0 + (Math.floor(i / nx) + .5) * step;
    if (!insideSite(level.plan, sx[i], sy[i])) continue;
    if (area && !ptInPoly(sx[i],sy[i],area.pts)) continue;
    const z = level.elevation + surface.height;
    if (!scene.trace({x:sx[i],y:sy[i],z:z-.001},{x:sx[i],y:sy[i],z:z+.001}).high) continue;
    inSite[i] = 1;
    const value = illuminanceAt(scene, { x: sx[i], y: sy[i], z: level.elevation + surface.height }, normal), state = lightingState(value, surface.minimumLux);
    low[i] = value.low; high[i] = value.high; states[i] = { met: 1, shortfall: 2, unknown: 3 }[state]; shade[i] = { sun: 1, shade: 2, unknown: 3, 'below-horizon': 4 }[value.shade] || 0;
    summary.samples++; summary[state]++; summary.min = Math.min(summary.min, value.low); summary.max = Math.max(summary.max, value.high); summary.mean += value.low;
    value.issues.forEach(v => issues.add(v));
  }
  summary.mean /= summary.samples || 1; if (!summary.samples) summary.min = null;
  return { G: { n, sx, sy, inSite, step }, low, high, shade, states, summary, issues: [...issues], floorId: level.id, floorName: level.name, surface, layoutReview: review };
}

export function lightingFingerprint(S) {
  const levels = [S, ...(S.floors || []).map(f => ({ ...f.plan, activeFloorId: f.id, elevation: f.elevation }))].sort((a, b) => String(a.activeFloorId).localeCompare(String(b.activeFloorId)));
  const { activeId, ...lighting } = S.lighting || {};
  // Illustrative camera night/IR controls do not change visible-light checks.
  const cameras = p => p.cams?.map(({ nightVision, ...camera }) => camera);
  // The unit system changes presentation only. Keeping it out of the physical
  // fingerprint lets a saved light check remain valid when users switch units.
  const options = p => { const { measurementSystem, ...physical } = p.opt || {}; return physical; };
  const text = JSON.stringify([LIGHTING_VERSION, lighting, S.stairs, levels.map(p => [p.activeFloorId, p.elevation || 0, p.areas, p.walls, p.openings, p.obstacles, p.sceneState, p.lights, p.targets, p.tasks, cameras(p), p.models, p.install, options(p)])]);
  let a = 2166136261, b = 5381;
  for (let i = 0; i < text.length; i++) { const c = text.charCodeAt(i); a = Math.imul(a ^ c, 16777619); b = Math.imul(b, 33) ^ c; }
  return `${LIGHTING_VERSION}:${(a >>> 0).toString(16)}:${(b >>> 0).toString(16)}`;
}
